// Real dean roster from the About > Deans section source set (deans.xlsx).
// Photos live in biet-media (sites/biet/deans/); <Figure> falls back to a
// placeholder for deans without one. dNo is the source-set numbering key.
//
// Four deans have no portrait in the deans source set but do appear in the
// staff directory, so they carry a `staffSlug` and borrow that record's photo
// via `deanPhoto()` — one image, not a second copy. Note the directory writes
// initials in the other order for some names (the dean sheet's
// "Dr. Mallikarjuna S B" is the directory's "S B Mallikarjuna"), which is why
// the link is an explicit slug rather than a name lookup.
import type { Media } from "@zivin-labs/site-content";
import { STAFF } from "./staff";

export interface Dean {
  dNo: string;
  name: string;
  role: string;
  photo?: Media;
  /** Staff-directory slug to borrow a portrait from, when `photo` is unset. */
  staffSlug?: string;
}

export const DEANS: Dean[] = [
  // Re-cropped from his staff-directory original, which framed him low with a
  // quarter of the picture as empty wall and a door edge at the right — in a
  // grid of tight head-and-shoulders portraits it read as the odd one out.
  { dNo: "D01", name: "Dr. Dinesh Y N", role: "Dean Academics", photo: { url: "sites/biet/deans/d01-dinesh-y-n.jpg", alt: "Dr. Dinesh Y N, Dean Academics", width: 794, height: 1020 } },
  { dNo: "D02", name: "Dr. Pradeep N", role: "Dean Academics", photo: { url: "sites/biet/deans/d02.jpg", alt: "Dr. Pradeep N, Dean Academics", width: 1991, height: 2560 } },
  { dNo: "D03", name: "Dr. Chidananda G", role: "Associate Dean Academics", photo: { url: "sites/biet/deans/d03.jpg", alt: "Dr. Chidananda G, Associate Dean Academics", width: 1991, height: 2560 } },
  { dNo: "D04", name: "Dr. Kumarappa S.", role: "Dean Examination", photo: { url: "sites/biet/deans/d04.jpg", alt: "Dr. Kumarappa S., Dean Examination", width: 1991, height: 2560 } },
  { dNo: "D05", name: "Dr. Anjaneya L H", role: "Associate Dean Examinations", photo: { url: "sites/biet/deans/d05.jpg", alt: "Dr. Anjaneya L H, Associate Dean Examinations", width: 1991, height: 2560 } },
  { dNo: "D06", name: "Dr. Nirmala C R", role: "Dean Training & Placement, Dean IT Cell", photo: { url: "sites/biet/deans/d06.jpg", alt: "Dr. Nirmala C R, Dean Training & Placement, Dean IT Cell", width: 1991, height: 2560 } },
  { dNo: "D07", name: "Dr. Vinutha H P", role: "Dean Incubation, Dean Student Welfare", photo: { url: "sites/biet/deans/d07.jpg", alt: "Dr. Vinutha H P, Dean Incubation, Dean Student Welfare", width: 1991, height: 2560 } },
  { dNo: "D08", name: "Dr. G.S. Sunitha", role: "Dean Research & Development (R & D)", staffSlug: "g-s-sunitha" },
  { dNo: "D09", name: "Dr. A.G. Shankara Murthy", role: "Dean Research & Development (R & D)", photo: { url: "sites/biet/deans/d09.jpg", alt: "Dr. A.G. Shankara Murthy, Dean Research & Development (R & D)", width: 1991, height: 2560 } },
  { dNo: "D10", name: "Dr. Leela G H", role: "Dean Admissions", photo: { url: "sites/biet/deans/d10.jpg", alt: "Dr. Leela G H, Dean Admissions", width: 1991, height: 2560 } },
  { dNo: "D11", name: "Dr. Mallikarjuna S B", role: "Dean Infrastructure", staffSlug: "s-b-mallikarjuna" },
  { dNo: "D12", name: "Dr. Devendrappa K C", role: "Dean Alumni Relations", photo: { url: "sites/biet/deans/d12.jpg", alt: "Dr. Devendrappa K C, Dean Alumni Relations", width: 270, height: 350 } },
  { dNo: "D13", name: "Smt. Ashalatha M.E", role: "Dean Women Welfare", photo: { url: "sites/biet/deans/d13.jpg", alt: "Smt. Ashalatha M.E, Dean Women Welfare", width: 1871, height: 1867 } },
  // Re-cropped from d14.jpg: at 0.667 the card cover-cropped 11% of the height,
  // and since his head starts 2.7% from the top that clipped his hair.
  { dNo: "D14", name: "Dr. Kalleshappa C M", role: "Dean Public Relations", photo: { url: "sites/biet/deans/d14-kalleshappa-c-m.jpg", alt: "Dr. Kalleshappa C M, Dean Public Relations", width: 588, height: 756 } },
  { dNo: "D15", name: "Dr. Sharan A.S", role: "Dean Skill Development", staffSlug: "sharan-a-s" },
];

/**
 * The dean's own portrait, falling back to their staff-directory photo.
 * Returns undefined when neither exists, so <Figure> shows its placeholder.
 */
export function deanPhoto(d: Dean): Media | undefined {
  if (d.photo) return d.photo;
  if (!d.staffSlug) return undefined;
  return STAFF.find((s) => s.slug === d.staffSlug)?.photo;
}
